import { useState } from 'react';
import { Loader2, Shield, X } from 'lucide-react';
import { Button } from '@lms/ui';
import type { User } from '@lms/shared/types';
import { useUpdateUser, useRoles } from '../hooks/useUsers';

interface AssignRoleModalProps {
  user: User;
  onClose: () => void;
}

export function AssignRoleModal({ user, onClose }: AssignRoleModalProps) {
  const { mutate: updateUser, isPending } = useUpdateUser();
  const { data: roles, isLoading: isLoadingRoles } = useRoles();

  // Initialize with current role if available
  const currentRoleName =
    user.roles && user.roles.length > 0
      ? typeof user.roles[0] === 'string'
        ? user.roles[0]
        : (user.roles[0] as { role: { name: string } }).role?.name
      : 'PARTICIPANT';

  const [selectedRole, setSelectedRole] = useState(currentRoleName);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateUser({ id: user.id, data: { role: selectedRole } }, { onSuccess: onClose });
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex justify-center p-4 animate-fadeIn overflow-y-auto py-12">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-md shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 my-auto">
        <div className="p-8 border-b border-outline-variant/10 bg-surface-container-low/30 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              Assign Authority
            </h2>
            <p className="text-xs font-body text-on-surface-variant mt-1 opacity-60 uppercase tracking-widest font-bold">
              Privilege Escalation & Control
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors"
          >
            <X size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="flex items-center gap-4 p-4 bg-surface-container-low/50 rounded-2xl border border-on-surface/5">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg uppercase">
              {user.name.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-bold text-on-surface m-0">{user.name}</p>
              <p className="text-[10px] font-mono text-on-surface-variant opacity-60 m-0">
                {user.email}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              Architectural Role
            </label>
            {isLoadingRoles ? (
              <div className="flex justify-center p-4">
                <Loader2 className="animate-spin text-primary" size={20} />
              </div>
            ) : (
              <div className="space-y-3">
                {roles?.map(role => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setSelectedRole(role.name)}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all
                       ${
                         selectedRole === role.name
                           ? 'bg-primary/5 border-primary text-primary shadow-sm'
                           : 'bg-surface-container-low border-on-surface/5 text-on-surface-variant hover:border-primary/20'
                       }`}
                  >
                    <div className="flex items-center gap-3">
                      <Shield
                        size={16}
                        className={
                          selectedRole === role.name ? 'text-primary' : 'text-on-surface-variant/40'
                        }
                      />
                      <div className="text-left">
                        <p className="text-xs font-bold uppercase tracking-widest m-0">
                          {role.name}
                        </p>
                        <p className="text-[9px] opacity-60 m-0 line-clamp-1">
                          {role.description || `Grants ${role.name} level access.`}
                        </p>
                      </div>
                    </div>
                    {selectedRole === role.name && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pt-6 flex justify-end gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-[10px] uppercase font-bold tracking-widest"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-xl px-8 text-[10px] uppercase font-bold tracking-[0.2em] shadow-level2"
              disabled={isPending || isLoadingRoles}
            >
              {isPending ? <Loader2 className="animate-spin" size={14} /> : 'Update Authority'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
